import type { Message, TipTapDoc, TipTapMark, TipTapNode } from "posthog-js";
import { cn } from "@nfp-tracker/utils";
import { Fragment, ReactNode } from "react";
import { isSafeImageUrl, isSafeLinkUrl, parseInlineMarkdown } from "../utils/richText";

const LINK_CLASSES = "underline underline-offset-2";
/** Long URLs and pasted error strings are the usual source of horizontal overflow. */
const WRAP_CLASSES = "break-words [overflow-wrap:anywhere]";

const renderMarks = (content: ReactNode, marks: TipTapMark[] | undefined): ReactNode => {
  if (!marks?.length) return content;

  // Applied outermost-last so nesting order matches the order the marks were declared in.
  return marks.reduce<ReactNode>((wrapped, mark) => {
    switch (mark.type) {
      case "bold":
        return <strong className="font-semibold">{wrapped}</strong>;
      case "italic":
        return <em>{wrapped}</em>;
      case "underline":
        return <u>{wrapped}</u>;
      case "strike":
        return <s>{wrapped}</s>;
      case "code":
        return (
          <code className={cn("rounded bg-black/10 px-1 py-0.5 font-mono text-xs", WRAP_CLASSES)}>
            {wrapped}
          </code>
        );
      case "link": {
        const href = typeof mark.attrs?.href === "string" ? mark.attrs.href : "";
        if (!isSafeLinkUrl(href)) return wrapped;

        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(LINK_CLASSES, WRAP_CLASSES)}
          >
            {wrapped}
          </a>
        );
      }
      default:
        return wrapped;
    }
  }, content);
};

const renderNodes = (nodes: TipTapNode[] | undefined): ReactNode =>
  nodes?.map((node, index) => <Fragment key={index}>{renderNode(node)}</Fragment>);

const renderNode = (node: TipTapNode): ReactNode => {
  switch (node.type) {
    case "text":
      return renderMarks(node.text ?? "", node.marks);

    case "hardBreak":
      return <br />;

    case "paragraph":
      return <p className={cn("whitespace-pre-wrap", WRAP_CLASSES)}>{renderNodes(node.content)}</p>;

    case "heading":
      return (
        <p className={cn("font-semibold", WRAP_CLASSES)}>{renderNodes(node.content)}</p>
      );

    case "image": {
      const src = typeof node.attrs?.src === "string" ? node.attrs.src : "";
      const alt = typeof node.attrs?.alt === "string" ? node.attrs.alt : "";
      if (!isSafeImageUrl(src)) return alt || null;

      return (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="my-1 max-h-64 max-w-full rounded-md object-contain"
        />
      );
    }

    case "bulletList":
      return <ul className="list-disc space-y-0.5 pl-5">{renderNodes(node.content)}</ul>;

    case "orderedList":
      return <ol className="list-decimal space-y-0.5 pl-5">{renderNodes(node.content)}</ol>;

    case "listItem":
      return <li className={WRAP_CLASSES}>{renderNodes(node.content)}</li>;

    case "blockquote":
      return (
        <blockquote className="border-l-2 border-current/30 pl-2 opacity-90">
          {renderNodes(node.content)}
        </blockquote>
      );

    case "codeBlock":
      return (
        <pre className="max-w-full overflow-x-auto rounded bg-black/10 p-2 font-mono text-xs">
          <code>{renderNodes(node.content)}</code>
        </pre>
      );

    case "horizontalRule":
      return <hr className="my-2 border-current/20" />;

    default:
      // Unknown node types still render their children rather than dropping the message.
      return renderNodes(node.content);
  }
};

const renderMarkdownFallback = (content: string): ReactNode =>
  parseInlineMarkdown(content).map((token, index) => {
    switch (token.type) {
      case "bold":
        return (
          <strong key={index} className="font-semibold">
            {token.value}
          </strong>
        );
      case "italic":
        return <em key={index}>{token.value}</em>;
      case "code":
        return (
          <code
            key={index}
            className={cn("rounded bg-black/10 px-1 py-0.5 font-mono text-xs", WRAP_CLASSES)}
          >
            {token.value}
          </code>
        );
      case "link":
        return (
          <a
            key={index}
            href={token.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(LINK_CLASSES, WRAP_CLASSES)}
          >
            {token.value}
          </a>
        );
      case "image":
        return (
          <img
            key={index}
            src={token.src}
            alt={token.alt}
            loading="lazy"
            className="my-1 max-h-64 max-w-full rounded-md object-contain"
          />
        );
      default:
        return <Fragment key={index}>{token.value}</Fragment>;
    }
  });

const hasRenderableRichContent = (richContent: TipTapDoc | undefined): boolean =>
  richContent?.type === "doc" && Array.isArray(richContent.content) && richContent.content.length > 0;

/**
 * Support replies are authored in a rich text editor, so they arrive as TipTap JSON in
 * `rich_content` with a plain-text `content` alongside it. We render the structured form
 * when it's there and fall back to parsing light markdown out of `content` — that fallback
 * is what stops bold/link/image syntax from showing up raw.
 */
export const MessageContent = ({ message }: { message: Message }) => (
  <div className={cn("space-y-1.5 text-sm", WRAP_CLASSES)}>
    {hasRenderableRichContent(message.rich_content)
      ? renderNodes(message.rich_content?.content)
      : renderMarkdownFallback(message.content)}
  </div>
);
